import React, { useContext } from "react";
import PropTypes from "prop-types";
import { expertUrl, companyUrl, toAbsoluteUrl } from "views_shared/app/javascript/utils/urls";
import AvatarProfileImage from "../../AvatarProfileImage";
import PersonHeader from "../../profiles/PersonHeader";
import ProfileHeaderLocation from "../../profiles/ProfileHeaderLocation";
import Qwoted100Badge from "../../profiles/Qwoted100Badge";
import useBadge from "../../profiles/use_badge";
import FollowControl from "../../profiles/FollowControl";
import ShareProfileButton from "../../profiles/ShareProfileButton";
import HeaderKebabMenu from "../../profiles/HeaderKebabMenu";
import MessageButton from "../../MessageButton";
import { ExpertContext } from "../data/expert_context_management";

const expertProfileBannerImage = RAILS_ASSET_URL("/expert-profile-banner.png");
const hybridProfileBannerImage = RAILS_ASSET_URL("/hybrid-profile-banner.png");

// A small inline edit pen for the header's name/headline (mirrors the PR page's). Renders only when the
// viewer may edit the entity; opens the given section's modal. `editable` arrives null rather than false
// for an anonymous viewer, so this is a truthiness check, never a comparison against false. The visual
// recipe matches SectionCard's own pen exactly (this page's section cards already use it) — copied
// verbatim rather than shared, since three near-identical local copies isn't the second-duplication
// signal that would justify extracting a component.
function HeaderEditPen({ section, label }) {
  const { viewerContext, setOpenEditModal } = useContext(ExpertContext);
  if (!viewerContext?.editable) return null;
  return (
    <button
      type="button"
      aria-label={label}
      className="btn btn-primary btn-sm ms-2 d-inline-flex align-items-center justify-content-center"
      style={{ width: "2rem", height: "2rem", padding: 0 }}
      onClick={() => setOpenEditModal(section)}
    >
      <i className="fa-solid fa-pencil-alt" />
    </button>
  );
}

HeaderEditPen.propTypes = {
  section: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

// Expert's engine header on the shared <PersonHeader> chrome — this same header serves both a plain
// expert and a hybrid (the data taxonomy differs, the visual chassis does not): a per-type banner band,
// the avatar (hybrid-aware label AND hybrid-aware overlap class), the identity column in the ruled order
// (name+pen -> title-at-workplace -> headline -> location -> badges), and the top-right actions row.
// Contact details and the message action no longer live here — they render in the rail's Connect card
// (see Expert.jsx), and Represented-by has moved out of the main column entirely into its own rail card.
function ExpertPersonHeader() {
  const { expert, expertId, hybrid, viewerContext, setOpenEditModal, silentReload, headshotPickerRequest } =
    useContext(ExpertContext);
  const isQwoted100Member = useBadge("qwoted100", "Id::Profile::Promoter", expert.self_represented_profile_id);
  const editable = !!viewerContext?.editable;

  // <AvatarWithLabel> stays the visual renderer (the ring + curved "expert"/"expert / pr" label);
  // <AvatarProfileImage> only adds the hover-revealed edit controls around it, and only for a viewer
  // who may edit — silentReload is the same "this saved, refetch" signal the rest of the edit surface
  // uses, so a saved headshot lands the same way a saved header field does.
  const avatar = (
    <AvatarProfileImage
      profileType={hybrid ? "promoter_hybrid" : "promoter_expert"}
      avatarClassName="border border-info border-4"
      size={160}
      editable={editable}
      imgOwnerId={expertId}
      imgOwnerName={expert.name}
      imgOwnerType="expert"
      profileImageUrl={expert.profile_image_url}
      onChange={silentReload}
      openPickerSignal={headshotPickerRequest}
    />
  );

  const banner = (
    <div className="person-header-banner">
      <img src={hybrid ? hybridProfileBannerImage : expertProfileBannerImage} alt="" />
    </div>
  );

  // One headline slot: custom headline > featured job title > most recent job title. The view
  // coalesces headline onto the pre-joined title string when no custom headline is set, so a
  // headline that differs from that string (trimmed) is custom and takes the slot alone.
  const hasCustomHeadline =
    !!(expert.headline || "").trim() &&
    (expert.headline || "").trim() !== (expert.featured_title_at_company || "").trim();

  // The linked branch always has content (it requires the title + company columns); the fallback is
  // the pre-joined string, which the view can hand back empty when the person has no visible job at
  // all — an unguarded heading would render a full-height empty <h2> in that case.
  const hasFeaturedTitleLink = !!(
    expert.featured_title &&
    expert.featured_company_slug &&
    expert.featured_company_name
  );
  const showFeaturedTitle = !hasCustomHeadline && (hasFeaturedTitleLink || !!expert.featured_title_at_company);

  // Ruled identity-stack order: name+pen -> headline -> location -> badges row.
  // Location was already rendered here before this round (location_string is a real column on the
  // experts view) and simply keeps its ruled position. The Qwoted 100 pill is new for this engine — it
  // is wired directly into the badges-row position, with no legacy placement to migrate away from.
  const identity = (
    <>
      <h1 className="h3 fw-bold mb-1">
        {expert.name}
        <HeaderEditPen section="HEADER" label="Edit Header" />
      </h1>
      {hasCustomHeadline && <h2 className="h5 text-body-secondary mb-1">{expert.headline}</h2>}
      {/* The derived title links out to the company page when the title + company columns are all
          present. Otherwise the pre-joined featured_title_at_company, which the view already composes
          down to just the company name (no " at ") when the job title is blank. */}
      {showFeaturedTitle && (
        <h2 className="h5 text-body-secondary mb-1">
          {hasFeaturedTitleLink ? (
            <>
              {expert.featured_title} at{" "}
              <a
                href={companyUrl({
                  slug: expert.featured_company_slug,
                  migrate_id: expert.featured_company_migrate_id,
                })}
              >
                {expert.featured_company_name}
              </a>
            </>
          ) : (
            expert.featured_title_at_company
          )}
        </h2>
      )}
      <ProfileHeaderLocation location={expert.location_string} />
      {isQwoted100Member && (
        <div className="mt-2">
          <Qwoted100Badge />
        </div>
      )}
    </>
  );

  const canonicalProfileUrl = toAbsoluteUrl(expertUrl({ slug: expert.slug, migrate_id: expert.migrate_id }));

  // Viewer-split actions row (below the banner, top-right): a viewer of someone else's profile gets
  // [Follow] [Share Profile]; an owner/admin gets [Share Profile] [kebab] instead — the kebab never
  // renders for a non-editor, so it never ships empty to the public. Both kebab items open the same
  // header edit modal (the visibility control lives inside it too).
  // Never yourself; anonymous viewers have no mailbox to send from.
  const messageButton =
    !viewerContext?.isSelf && !!viewerContext?.isAuthenticated ? (
      <MessageButton
        promoEntityId={expertId}
        promoEntityType="Id::PromoEntity::Expert"
        openInboxThread
        iconClassName="fa-regular fa-message"
      />
    ) : null;

  const actions = editable ? (
    <>
      {messageButton}
      <ShareProfileButton url={canonicalProfileUrl} />
      <HeaderKebabMenu
        onEditProfile={() => setOpenEditModal("HEADER")}
        onEditVisibility={() => setOpenEditModal("HEADER")}
      />
    </>
  ) : (
    <>
      <FollowControl followableType="Id::PromoEntity::Expert" followableId={expertId} />
      {messageButton}
      <ShareProfileButton url={canonicalProfileUrl} />
    </>
  );

  return (
    <PersonHeader
      avatar={avatar}
      identity={identity}
      actions={actions}
      banner={banner}
      avatarWrapperClassName={hybrid ? "hybrid-avatar-frame" : "expert-avatar-frame"}
    />
  );
}

export default ExpertPersonHeader;
